import React, {useCallback, useEffect, useRef, useState} from 'react'
import {selectKP11SelectCartCentersNApps} from '../../../../../../app-store/kp11CartSlice'
import {useSelector} from 'react-redux'
import {
  KPStepperWrapperStyle,
  StepperWrapper,
  CardPlanAddToCartStyle,
} from '../kp-11Link-catalog-cards-style'
import {DELAY_HIDE_IN_SEC} from '../../../../../../utils/constants'
import {
  buildCartEntity,
  // buildCartEntity,
  countryCurrencyEnum,
} from '../../../kp-11Link-utility/kp11-utility'
import {KP11KeapSocialAdsBronze} from './kp-11-thryv-add-ons-cards/kp-11-social-ads-bronze'
import {kpSuccessIcon} from '../../../../../../assets'
import {KP11KeapSocialAdsSilver} from './kp-11-thryv-add-ons-cards/kp-11-social-ads-silver'
import {KP11KeapSocialAdsGold} from './kp-11-thryv-add-ons-cards/kp-11-social-ads-gold'

const getProductPrice = (prices, country = 'US') => {
  const currency = countryCurrencyEnum[country] || ''
  const price = prices?.find(p => p.currency === currency)
  return price ? price.unitAmount : null
}

/* ----------------------- Stepper ----------------------- */
const KPAdsStepper = ({
  label,
  value = 0,
  min = 0,
  max = 5,
  step = 1,
  onChange,
  unitLabel,
  disabled = false,
  defaultDisableValue,
}) => {
  const handleDecrement = useCallback(() => {
    if (!disabled && value > min) onChange(value - step)
  }, [value, min, step, disabled, onChange])

  const handleIncrement = useCallback(() => {
    if (!disabled && value < max) onChange(value + step)
  }, [value, max, step, disabled, onChange])

  return (
    <StepperWrapper className={disabled ? 'disabled' : ''}>
      {label && <label className="stepper-label">{label}</label>}

      <div className="stepper-box">
        <button
          type="button"
          className="step-btn minus"
          onClick={handleDecrement}
          disabled={disabled || value <= defaultDisableValue || value <= min}
        >
          –
        </button>

        <div className="step-value">{value}</div>

        <button
          type="button"
          className="step-btn plus"
          onClick={handleIncrement}
          disabled={disabled || value >= max}
        >
          +
        </button>
      </div>

      {unitLabel && <span className="unit-label">{unitLabel}</span>}
    </StepperWrapper>
  )
}

/* ----------------------- Individual Ads Item ----------------------- */
export const SocialAdsItem = ({
  selectedCountry,
  item,
  selectedDefaultItem,
  selectedItem,
  onQtyChange,
  disabled,
}) => {
  const [qty, setQty] = useState(selectedItem?.quantity || 0)
  const isUserInteraction = useRef(false)

  useEffect(() => {
    // Update qty from props, but don't trigger update callback
    if (!isUserInteraction.current) {
      setQty(selectedItem?.quantity || 0)
    }
    isUserInteraction.current = false
  }, [selectedItem])

  const getUnitLabelAmount = item => {
    if (!item) return ''
    const currency = countryCurrencyEnum[selectedCountry.toUpperCase()]
    const matched = item.currencies?.find(c => c.currency === currency)
    const eachItemAmount = matched
      ? matched.unitAmount
      : item.currencies?.[0]?.unitAmount || 0
    return eachItemAmount
  }

  const buildAddon = (selectedDefaultItem, selectedItem, qty) => {
    const amount =
      qty * (selectedItem.unitAmount || getUnitLabelAmount(item) || 0)
    let updatedAmount = amount
    if (selectedDefaultItem && selectedDefaultItem?.quantity > 0) {
      updatedAmount =
        selectedDefaultItem?.amount < amount
          ? amount - selectedDefaultItem?.amount
          : selectedDefaultItem?.amount
    }

    let updatedItem = {
      code: selectedItem?.code,
      name: selectedItem?.name,
      unitAmount: selectedItem?.unitAmount || getUnitLabelAmount(item) || 0,
      quantity: qty,
      amount: updatedAmount,
      isAlreadyAdded: selectedItem?.isAlreadyAdded,
      isUpgraded: selectedItem?.isAlreadyAdded,
      groupKey: selectedItem?.groupKey || item?.groupKey, // Preserve groupKey
      section: selectedItem?.section || item?.section, // Preserve section
    }
    if (
      selectedDefaultItem?.amount === amount ||
      selectedDefaultItem?.amount > amount
    ) {
      updatedItem = selectedDefaultItem
    }
    return updatedItem
  }

  const handleQtyChange = newQty => {
    isUserInteraction.current = true
    setQty(newQty)
    const updatedItem = buildAddon(selectedDefaultItem, selectedItem, newQty)
    onQtyChange(updatedItem)
  }

  const getShortLabel = name => {
    if (!name) return ''
    const parts = name.split(' ')
    return parts[parts.length - 1]
  }

  return (
    <KPStepperWrapperStyle>
      <KPAdsStepper
        label={getShortLabel(item.name)}
        value={qty}
        min={0}
        max={5}
        step={1}
        onChange={handleQtyChange}
        unitLabel={`$${getUnitLabelAmount(item)} each`}
        disabled={disabled}
        defaultDisableValue={selectedDefaultItem?.quantity || 0}
      />
    </KPStepperWrapperStyle>
  )
}

/* ----------------------- Grouped Ads item Card ----------------------- */
export const KP11LinkSocialAdsCardComponent = ({
  selectedCountry = 'US',
  groupKey = 'thryvAddons',
  items = [],
  disabled,
  addedToCart,
  onSelectItem,
  isChildComponent = false,
  onGroupCustomFieldsUpdate,
  groupCustomFields,
  disabledItems = {},
}) => {
  // Get cart items from Redux
  const cartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const [addToCartBanner, setAddToCartBanner] = useState(false)

  // Auto-hide "Added to cart" banner
  useEffect(() => {
    if (addedToCart) {
      setAddToCartBanner(true)
      const timer = setTimeout(
        () => setAddToCartBanner(false),
        DELAY_HIDE_IN_SEC * 1000,
      )
      return () => clearTimeout(timer)
    }
  }, [addedToCart])

  return (
    <>
      {items.length > 0 &&
        items.map(item => {
          let addonDetail = cartCentersAndApps?.apps?.find(
            addon => addon.code === item.code,
          )
          if (item.code === 'addon-socialadsbronze-5-m2m') {
            return (
              <KP11KeapSocialAdsBronze
                key={item.code}
                name={item.name}
                disabled={disabledItems[item.code] || false}
                price={getProductPrice(item?.currencies, selectedCountry)}
                onSave={finalValue => {
                  const updatedItem = buildCartEntity({
                    source: {
                      ...item,
                      groupKey,
                    },
                    quantity: finalValue,
                  })

                  onSelectItem(updatedItem)
                  onGroupCustomFieldsUpdate({
                    socialAdsBronzeValue: finalValue,
                  })
                }}
                quantity={addonDetail?.quantity || 0}
              />
            )
          }
          if (item.code === 'addon-socialadssilver-5-m2m') {
            return (
              <KP11KeapSocialAdsSilver
                key={item.code}
                name={item.name}
                price={getProductPrice(item?.currencies, selectedCountry)}
                disabled={disabledItems[item.code] || false}
                onSave={finalValue => {
                  const updatedItem = buildCartEntity({
                    source: {
                      ...item,
                      groupKey,
                    },
                    quantity: finalValue,
                  })

                  onSelectItem(updatedItem)
                  onGroupCustomFieldsUpdate({
                    socialAdsSilverValue: finalValue,
                  })
                }}
                quantity={addonDetail?.quantity || 0}
              />
            )
          }
          if (item.code === 'addon-socialadsgold-5-m2m') {
            return (
              <KP11KeapSocialAdsGold
                key={item.code}
                name={item.name}
                price={getProductPrice(item?.currencies, selectedCountry)}
                disabled={disabledItems[item.code] || false}
                onSave={finalValue => {
                  const updatedItem = buildCartEntity({
                    source: {
                      ...item,
                      groupKey,
                    },
                    quantity: finalValue,
                  })

                  onSelectItem(updatedItem)
                  onGroupCustomFieldsUpdate({
                    socialAdsGoldValue: finalValue,
                  })
                }}
                quantity={addonDetail?.quantity || 0}
              />
            )
          }
          return null
        })}

      {addToCartBanner && (
        <CardPlanAddToCartStyle>
          <img src={kpSuccessIcon} alt="success icon" />
          <span>Added to cart</span>
        </CardPlanAddToCartStyle>
      )}
    </>
  )
}
